import { lazy, Suspense, useState } from 'react'
import { useQueryClient, useSuspenseQueries } from '@tanstack/react-query'
import {
	CircleDollarSignIcon,
	InfoIcon,
	PlusIcon,
	UploadIcon,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { useSelectedCurrency } from '@/entities/currency'
import {
	getOptimizationsMap,
	optimizationQueries,
} from '@/entities/optimization'
import { optimizationGroupQueries } from '@/entities/optimization-group'
import { optimizationReviewsQueries } from '@/entities/optimization-review'
import {
	OptimizationScheduleWidget,
	schedulesQueries,
} from '@/entities/optimization-schedule'
import { pricingAlertsQueries } from '@/entities/price-alert'
import { Access } from '@/entities/session'
import { VideoTutorials } from '@/entities/video-tutorial'

import { GlobalPricingAlertsTrigger } from '@/widgets/global-pricing-alerts-trigger'

import DocumentTitle from '@/shared/components/DocumentTitle'
import { ErrorBoundaryWithUser } from '@/shared/components/ErrorBoundary'
import ParentSize from '@/shared/components/ParentSize'
import NoData from '@/shared/components/placeholders/NoData'
import SearchField from '@/shared/components/SearchField'
import {
	Breadcrumb,
	BreadcrumbItem,
	BreadcrumbList,
	BreadcrumbPage,
} from '@/shared/components/ui/breadcrumb'
import { Button } from '@/shared/components/ui/button'
import { Content, Header } from '@/shared/components/ui/layout'
import { Separator } from '@/shared/components/ui/separator'
import { Skeleton } from '@/shared/components/ui/skeleton'
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from '@/shared/components/ui/tooltip'
import { AlertGroupBy, VideoTag } from '@/shared/generated'
import { useLocationQuery } from '@/shared/hooks/useLocationQuery'
import { ampli } from '@/shared/lib/ampli'
import { useModals } from '@/shared/lib/modal-manager'

import { optimizationGroupsQueries as optimizationGroupsMapQueries } from '../api/queries'
import { SkeletonLoad } from './loaders'
import OptimizationGroupsFilters from './OptimizationGroupsFilters'
import {
	OptimizationGroupsProvider,
	useOptimizationGroupsContext,
} from './OptimizationGroupsProvider'
import { OptimizationGroupsStatusesManager } from './OptimizationGroupsStatusesManager'
import OptimizationGroupsTable from './OptimizationGroupsTable'
import UassigneProductsBage from './UassignedProductsBage'
import ViewSwitcher from './ViewSwitcher'

const OptimizationGroupsTreeChart = lazy(() =>
	import('./OptimizationGroupsTreeChart').then((module) => ({
		default: module.OptimizationGroupsTreeChart,
	}))
)
function OptimizationGroupsContent() {
	const [view, setView] = useState<'table' | 'tree'>('table')
	const queryClient = useQueryClient()
	const currency = useSelectedCurrency()
	const data = useSuspenseQueries({
		queries: [
			optimizationGroupQueries.list(),
			schedulesQueries.list(),
			{
				...optimizationQueries.list(),
				refetchOnMount: 'always',
			},
			optimizationGroupQueries.salesPoints(),
			optimizationGroupsMapQueries.productGroupsMap(queryClient),
			optimizationReviewsQueries.list(),
			{
				...pricingAlertsQueries.list({
					group_by: AlertGroupBy.OptimizationGroupId,
					currency,
				}),
				refetchOnMount: 'always',
			},
		],
		combine: ([
			optimizationGroups,
			schedules,
			optimizations,
			salesEntities,
			productGroupsMap,
			reviews,
			alerts,
		]) => ({
			optimizationGroups: optimizationGroups.data,
			schedules: schedules.data,
			optimizations: getOptimizationsMap(optimizations.data),
			salesEntities: salesEntities.data,
			productGroupsMap: productGroupsMap.data,
			reviews: reviews.data,
			alerts: alerts.data,
		}),
	})

	return (
		<OptimizationGroupsProvider data={data}>
			<Header className='relative flex items-center gap-4 border-y bg-secondary px-4 py-2'>
				<OptimizationGroupsStatusesManager />
				<Separator orientation='vertical' className='h-5!' />
				<OptimizationScheduleWidget />
			</Header>
			<Header className='flex w-full items-center justify-between px-4 py-2'>
				<div className='flex items-center gap-2'>
					<Search />
					<OptimizationGroupsFilters />
				</div>
				<div className='flex items-center gap-2'>
					<UassigneProductsBage />
					<Actions />
					<Separator orientation='vertical' className='h-5!' />
					<GlobalPricingAlertsTrigger />
					<ViewSwitcher
						value={view}
						onChange={(v) => setView(v as 'table' | 'tree')}
					/>
				</div>
			</Header>
			<Content className='overflow-hidden px-4 pb-4'>
				{view === 'table' && <OptimizationGroupsTableTab />}
				{view === 'tree' && <OptimizationGroupsTreeTab />}
			</Content>
		</OptimizationGroupsProvider>
	)
}
const Search = () => {
	const { t } = useTranslation()

	const [{ search }, setQuery] = useLocationQuery()

	return (
		<SearchField
			value={search}
			compact
			onValueChange={(text) => {
				ampli.massRepricingSearch()
				setQuery({ search: text })
			}}
			placeholder={t('general_search')}
		/>
	)
}
const Actions = () => {
	const { t } = useTranslation()

	const { getSelectedItems, dataList, setRowSelection, selectionCount } =
		useOptimizationGroupsContext()
	const { showModal } = useModals()
	const resetSelection = () => setRowSelection({})
	const handleOpenBulkImport = () => {
		ampli.ogListImportFromFileClick()
		const items = selectionCount > 0 ? getSelectedItems() : dataList
		showModal('OPTIMIZATION_GROUPS_BULK_IMPORT', {
			items,
			optimizationGroupsCount: dataList.length,
			onResetSelection: resetSelection,
		})
	}

	return (
		<div className='flex items-center space-x-2'>
			<Tooltip>
				<TooltipTrigger asChild>
					<Button
						data-testid='bulk-og-import-button'
						onClick={handleOpenBulkImport}
					>
						<UploadIcon />
						{`${t('app.update_data', { defaultValue: 'Update data' })}` +
							(selectionCount ? ` (${selectionCount})` : '')}
					</Button>
				</TooltipTrigger>
				<TooltipContent>{t('optimization_groups_bulk_tooltip')}</TooltipContent>
			</Tooltip>
			<Access permissions={['MANAGE_OPTIMIZATION_GROUP']}>
				<Tooltip>
					<TooltipTrigger asChild>
						<Button
							size='icon'
							data-testid='create-og-trigger'
							variant='primary'
							onClick={() => {
								showModal('CREATE_OPTIMIZATION_GROUP')
								ampli.ogListCreateOgClick()
							}}
						>
							<PlusIcon />
						</Button>
					</TooltipTrigger>
					<TooltipContent>
						{t('og.create.tooltip', {
							defaultValue: 'Create optimization group',
						})}
					</TooltipContent>
				</Tooltip>
			</Access>
		</div>
	)
}
const OptimizationGroupsTableTab = () => {
	const { t } = useTranslation()

	const { dataList, rowSelection, setRowSelection } =
		useOptimizationGroupsContext()

	return (
		<div className='border-base flex h-full flex-1 flex-col overflow-hidden rounded-lg border border-solid'>
			{dataList.length > 0 ? (
				<OptimizationGroupsTable
					rowSelection={rowSelection}
					setRowSelection={setRowSelection}
					data={dataList}
				/>
			) : (
				<NoData
					subtitle={t('optimization_groups_no_data_subtitle', {
						defaultValue: 'Try to change filters or search query',
					})}
				/>
			)}
		</div>
	)
}
const OptimizationGroupsTreeTab = () => {
	const { t } = useTranslation()

	const { dataList } = useOptimizationGroupsContext()

	return (
		<div className='flex h-full flex-1 flex-col overflow-hidden'>
			{dataList.length > 0 ? (
				<ParentSize>
					{({ height }) => (
						<Suspense fallback={<SkeletonLoad />}>
							<OptimizationGroupsTreeChart
								height={height - 30}
								data={dataList}
							/>
						</Suspense>
					)}
				</ParentSize>
			) : (
				<NoData
					subtitle={t('optimization_groups_no_data_subtitle', {
						defaultValue: 'Try to change filters or search query',
					})}
				/>
			)}
		</div>
	)
}
function OptimizationGroupsPage() {
	const { t } = useTranslation()

	return (
		<DocumentTitle title={t('optimization_groups_title_page')}>
			<ErrorBoundaryWithUser showDialog>
				<Header className='h-11 items-center px-4 py-2'>
					<CircleDollarSignIcon className='size-4' />
					<Separator orientation='vertical' className='mx-2 h-5' />
					<Breadcrumb className='text-sm'>
						<BreadcrumbList>
							<BreadcrumbItem>
								<BreadcrumbPage>
									{t('menu.og', { defaultValue: 'Optimization groups' })}
								</BreadcrumbPage>
							</BreadcrumbItem>
						</BreadcrumbList>
					</Breadcrumb>
					<VideoTutorials tags={[VideoTag.OptimizationGroupList]}>
						<Button size='icon' className='ml-auto' variant='ghost'>
							<InfoIcon />
						</Button>
					</VideoTutorials>
				</Header>
				<Suspense
					fallback={
						<div>
							<Header className='relative flex items-center gap-4 border-y bg-secondary px-4 py-2'>
								<div className='flex h-14 gap-4'>
									<Skeleton className='h-14 w-12' />
									<Skeleton className='h-14 w-24' />
									<Skeleton className='h-14 w-28' />
									<Skeleton className='h-14 w-28' />
									<Skeleton className='h-14 w-28' />
									<Skeleton className='h-14 w-32' />
									<Skeleton className='h-14 w-48' />
								</div>
							</Header>
							<Header className='flex w-full items-center justify-between px-4 py-2'>
								<Skeleton className='h-8 w-32' />
							</Header>
							<Content className='overflow-hidden px-4 pb-4'>
								<div className='border-base flex h-full flex-1 flex-col overflow-hidden rounded-lg border border-solid'>
									<SkeletonLoad />
								</div>
							</Content>
						</div>
					}
				>
					<OptimizationGroupsContent />
				</Suspense>
			</ErrorBoundaryWithUser>
		</DocumentTitle>
	)
}

export default OptimizationGroupsPage
